import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { submitValuation } from "@/lib/api";

export const ValuationForm = ({ compact = false }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", intent: "seller" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.address) {
      toast.error("Please add your name, email and property address.");
      return;
    }
    setLoading(true);
    try {
      await submitValuation(form);
      setDone(true);
      toast.success("Request sent! Brett will reach out shortly.");
    } catch {
      toast.error("Something went wrong. Please call 720-776-2502.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="valuation-success" className="bg-kw-ink text-white p-8">
        <p className="overline text-kw-red mb-3">Request received</p>
        <h3 className="font-serif text-3xl mb-2">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="text-neutral-300 text-sm leading-relaxed">
          Brett is preparing your personalized home value strategy and will contact you at{" "}
          {form.email} shortly.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="valuation-form"
      className={`bg-white border border-neutral-200 ${compact ? "p-6" : "p-7 sm:p-8 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.25)]"}`}
    >
      <p className="overline text-kw-red mb-1">Free Home Valuation</p>
      <h3 className="font-serif text-2xl sm:text-3xl tracking-tight mb-5 text-kw-ink">
        What's your home worth?
      </h3>

      <div className="space-y-3">
        <input
          data-testid="valuation-address-input"
          value={form.address}
          onChange={set("address")}
          placeholder="Property address"
          className="w-full border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-kw-red focus:ring-1 focus:ring-kw-red transition-colors"
        />
        <div className="grid grid-cols-2 gap-3">
          <input
            data-testid="valuation-name-input"
            value={form.name}
            onChange={set("name")}
            placeholder="Full name"
            className="w-full border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-kw-red focus:ring-1 focus:ring-kw-red transition-colors"
          />
          <input
            data-testid="valuation-phone-input"
            value={form.phone}
            onChange={set("phone")}
            placeholder="Phone"
            className="w-full border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-kw-red focus:ring-1 focus:ring-kw-red transition-colors"
          />
        </div>
        <input
          data-testid="valuation-email-input"
          value={form.email}
          onChange={set("email")}
          placeholder="Email address"
          className="w-full border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-kw-red focus:ring-1 focus:ring-kw-red transition-colors"
        />
        <select
          data-testid="valuation-intent-select"
          value={form.intent}
          onChange={set("intent")}
          className="w-full border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-kw-red focus:ring-1 focus:ring-kw-red transition-colors bg-white text-neutral-700"
        >
          <option value="seller">I'm looking to sell</option>
          <option value="buyer">I'm looking to buy</option>
          <option value="both">Buying and selling</option>
        </select>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="valuation-submit-button"
        className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-kw-red hover:bg-kw-redhover disabled:opacity-70 text-white py-3.5 font-semibold tracking-wide transition-colors duration-300"
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : <>Get My Home Value <ArrowRight size={17} /></>}
      </button>
      <p className="mt-3 text-xs text-neutral-400">No spam. Just a strategic, no-obligation valuation.</p>
    </form>
  );
};
